import { AuthConfig } from '@/client/types/AuthConfig';
import { Config } from '@/client/types/Config';
import { useConsentStore } from '@/client/services/state/consentStore';
import { useWidgetSessionStore } from '@/client/services/state/widgetSessionStore';

const timeConvert = (date: number) => {
  const dateValue = new Date(date).valueOf();

  return parseInt(dateValue.toString().slice(0, -3), 10);
};

export const useIntercom = () => {
  const bootIntercomUser = (config: Config, authConfig: AuthConfig) => {
    if (!useConsentStore.getState().has('functional')) {
      return;
    }

    const { intercomAppId } = config;
    const { company, user } = authConfig;

    if (
      !intercomAppId ||
      !user ||
      !user._id ||
      !user.email ||
      !user.first_name ||
      !company ||
      !company.name
    ) {
      return;
    }

    const { intercomBootedUserId, setIntercomBootedUserId } = useWidgetSessionStore.getState();

    if (intercomBootedUserId === user._id) {
      return;
    }

    // Intercom re-identifies only on shutdown-then-boot; a bare second boot
    // keeps the previous user's conversation attached.
    if (intercomBootedUserId) {
      window.Intercom && window.Intercom('shutdown');
    }

    const created = user.first_login || user.createdAt || user.created_at;

    window.Intercom &&
      window.Intercom('boot', {
        app_id: intercomAppId,
        user_id: user._id,
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        created_at: timeConvert(+created),
        role: user.role,
        department: user.department,
        is_manager: !!user.is_manager,
        manager_email: user.manager_email ? user.manager_email : '',
        has_avatar: !!user.image,
        banner_image: !!user.banner_image,
        about: !!user.about,
        skills: !!(user.skills && user.skills.length),
        company: {
          id: company._id,
          name: company.name,
          created_at: timeConvert(+company.createdAt),
        },
        widget: {
          activator: '#IntercomDefaultWidget',
        },
        hide_default_launcher: true,
      });

    setIntercomBootedUserId(user._id);
  };

  const intercomUpdate = () => {
    if (!useWidgetSessionStore.getState().intercomBootedUserId) {
      return;
    }

    setTimeout(() => {
      window.Intercom && window.Intercom('update');
    }, 3);
  };

  const intercomShutdown = () => {
    const { intercomBootedUserId, setIntercomBootedUserId } = useWidgetSessionStore.getState();

    if (!intercomBootedUserId) {
      return;
    }

    window.Intercom && window.Intercom('shutdown');
    setIntercomBootedUserId(null);
  };

  return {
    bootIntercomUser,
    intercomUpdate,
    intercomShutdown,
  };
};
